import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import CampaignAttributionReport from "./CampaignAttributionReport";
import LandingFunnelReport from "./LandingFunnelReport";
import LanguageDemandReport from "./LanguageDemandReport";
import copy from "../../public/locales/en/analytics.json";
import { useAppDispatch, useAppSelector } from "../store";
import {
  fetchCampaignAttribution,
  fetchLandingFunnel,
  fetchLanguageDemand,
  fetchProductAnalytics,
  setAnalyticsPeriod,
} from "../store/slices/productAnalyticsSlice";
import { analyticsRatio } from "../utils/analyticsFormatting";

import type { AnalyticsPeriod, UserActivitySegment } from "@lingoshade/domain";

const segments = [
  ["total", "registered"],
  ["neverPracticed", "never"],
  ["active7", "active"],
  ["inactive", "inactive"],
  ["regular", "regular"],
] as const satisfies ReadonlyArray<
  readonly [keyof typeof copy.audienceLabels, UserActivitySegment]
>;

export default function ProductAnalyticsReport(): React.ReactElement {
  const dispatch = useAppDispatch();
  const { report, days, loading, error } = useAppSelector(
    (state) => state.productAnalytics,
  );
  const [tab, setTab] = useState(0);
  useEffect(() => {
    const request = dispatch(fetchProductAnalytics(days));
    return (): void => {
      request.abort();
    };
  }, [dispatch, days]);
  const percentage = (value: number): string =>
    analyticsRatio(
      report?.cohort.registered ? value / report.cohort.registered : null,
    );
  return (
    <>
      <Paper component="section" sx={{ p: { xs: 2, md: 3 }, mt: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          {copy.title}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {copy.description}
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
          <ToggleButtonGroup
            exclusive
            value={days}
            aria-label={copy.period}
            onChange={(_event, value: AnalyticsPeriod | null): void => {
              if (value !== null && value !== days)
                dispatch(setAnalyticsPeriod(value));
            }}
          >
            {([1, 7, 30, "all"] as const).map((period) => (
              <ToggleButton key={period} value={period}>
                {copy[`days${period}`]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Button
            disabled={loading}
            onClick={(): void => {
              void dispatch(fetchProductAnalytics(days));
              void dispatch(fetchCampaignAttribution(days));
              if (tab === 3) void dispatch(fetchLandingFunnel(days));
              if (tab === 4) void dispatch(fetchLanguageDemand(days));
            }}
          >
            {copy.refresh}
          </Button>
        </Box>
        <Tabs
          value={tab}
          onChange={(_event, value: number): void => setTab(value)}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <Tab label={copy.usage.title} />
          <Tab label={copy.campaign.title} />
          <Tab label={copy.payments} />
          <Tab label={copy.funnel.title} />
          <Tab label={copy.demand.title} />
        </Tabs>
        {loading && <CircularProgress size={28} sx={{ mt: 2 }} />}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {copy.error}
          </Alert>
        )}
        {report && tab === 0 && (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                gap: 2,
                my: 3,
              }}
            >
              {(["registered", "practiced", "paid"] as const).map((key) => (
                <Box
                  key={key}
                  sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}
                >
                  <Typography color="text.secondary">{copy[key]}</Typography>
                  <Typography variant="h4">{report.cohort[key]}</Typography>
                  {key !== "registered" && (
                    <Typography>{percentage(report.cohort[key])}</Typography>
                  )}
                </Box>
              ))}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {copy.cohortNote}
            </Typography>
            <Typography component="h3" variant="h6" sx={{ mt: 3, mb: 2 }}>
              {copy.audienceTitle}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)" },
                gap: 2,
              }}
            >
              {segments.map(([key, segment]) => (
                <Box
                  key={key}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Button
                    component={Link}
                    to={`/users?segment=${segment}`}
                    sx={{ p: 0, textAlign: "left" }}
                  >
                    {copy.audienceLabels[key]}
                  </Button>
                  <Typography variant="h4">{report.audience[key]}</Typography>
                  <Typography color="text.secondary">
                    {analyticsRatio(
                      report.audience.total
                        ? report.audience[key] / report.audience.total
                        : null,
                    )}
                  </Typography>
                </Box>
              ))}
              <Box sx={{ p: 2 }}>
                <Typography>{copy.returning}</Typography>
                <Typography variant="h4">
                  {report.audience.returning}
                </Typography>
                <Typography>
                  {analyticsRatio(
                    report.audience.activeInPeriod
                      ? report.audience.returning /
                          report.audience.activeInPeriod
                      : null,
                  )}
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {copy.audienceNote}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {copy.returningNote}
            </Typography>
            <Typography component="h3" variant="h6" sx={{ mt: 3 }}>
              {copy.trend}
            </Typography>
            <Box sx={{ height: 300, mt: 2 }} role="img" aria-label={copy.trend}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={report.days.map((day) => ({
                    ...day,
                    landingViews:
                      report.landing.firstRecordedDate &&
                      day.date >= report.landing.firstRecordedDate
                        ? day.landingViews
                        : null,
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" minTickGap={30} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="landingViews"
                    name={copy.views}
                    stroke="#78909c"
                    dot={false}
                    hide={!report.landing.firstRecordedDate}
                  />
                  <Line
                    type="monotone"
                    dataKey="registrations"
                    name={copy.registered}
                    stroke="#1976d2"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="activeUsers"
                    name={copy.activeUsers}
                    stroke="#2e7d32"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
                gap: 2,
                my: 3,
              }}
            >
              {(
                Object.keys(report.usage) as Array<keyof typeof report.usage>
              ).map((key) => (
                <Box key={key}>
                  <Typography color="text.secondary">
                    {copy.usage[key]}
                  </Typography>
                  <Typography variant="h5">{report.usage[key]}</Typography>
                </Box>
              ))}
              <Box>
                <Typography color="text.secondary">
                  {copy.newSubscriptions}
                </Typography>
                <Typography variant="h5">{report.newSubscriptions}</Typography>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {copy.usage.note}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {copy.subscriptionsNote}
            </Typography>
            <Alert severity="info" sx={{ my: 2 }}>
              {copy.trafficNote}
            </Alert>
            {!report.landing.configured && (
              <Alert severity="warning">{copy.unconfigured}</Alert>
            )}
            <Typography sx={{ my: 2 }}>
              {report.landing.firstRecordedDate
                ? `${copy.firstRecorded} ${report.landing.firstRecordedDate}`
                : copy.noTraffic}
            </Typography>
            <Accordion>
              <AccordionSummary>{copy.dailyDetails}</AccordionSummary>
              <AccordionDetails>
                <TableContainer>
                  <Table size="small" aria-label={copy.title}>
                    <TableHead>
                      <TableRow>
                        {[
                          copy.date,
                          copy.views,
                          copy.registered,
                          copy.listening,
                          copy.dictations,
                          copy.reviews,
                          copy.activeUsers,
                        ].map((label) => (
                          <TableCell key={label}>{label}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.days
                        .slice()
                        .reverse()
                        .map((day) => (
                          <TableRow key={day.date}>
                            <TableCell component="th" scope="row">
                              {day.date}
                            </TableCell>
                            <TableCell>
                              {report.landing.firstRecordedDate &&
                              day.date >= report.landing.firstRecordedDate
                                ? day.landingViews
                                : "—"}
                            </TableCell>
                            <TableCell>{day.registrations}</TableCell>
                            <TableCell>{day.listeningSessions}</TableCell>
                            <TableCell>{day.completedDictations}</TableCell>
                            <TableCell>{day.wordReviews}</TableCell>
                            <TableCell>{day.activeUsers}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </>
        )}
        {report && tab === 2 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
              {copy.paymentsNote}
            </Typography>
            {report.recordedPayments.length === 0 ? (
              <Typography>{copy.emptyPayments}</Typography>
            ) : (
              <TableContainer>
                <Table size="small" aria-label={copy.payments}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{copy.currency}</TableCell>
                      <TableCell>{copy.count}</TableCell>
                      <TableCell>{copy.amount}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.recordedPayments.map((row) => (
                      <TableRow key={row.currency}>
                        <TableCell>{row.currency}</TableCell>
                        <TableCell>{row.count}</TableCell>
                        <TableCell>
                          {row.amount.toLocaleString("en", {
                            maximumFractionDigits: 2,
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>
      {tab === 1 && <CampaignAttributionReport />}
      {tab === 3 && <LandingFunnelReport />}
      {tab === 4 && <LanguageDemandReport />}
    </>
  );
}
